import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AgentWelcomePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/public/agents/welcome/${token}`);
        setData(d);
        if (d.already_set) setDone(true);
      } catch {
        setNotFound(true);
      }
    })();
  }, [token]);

  const submit = async () => {
    if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pw !== pw2) { toast.error("Passwords don't match"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/public/agents/welcome/${token}`, { password: pw });
      setDone(true);
      toast.success("Password set — redirecting to login");
      setTimeout(() => navigate("/login"), 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to set password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-6">
      <Card className="max-w-md w-full p-10 bg-white border border-[#E9ECEF] rounded-sm">
        {notFound ? (
          <>
            <div className="text-[11px] uppercase tracking-[0.28em] text-red-600 font-bold">Link expired</div>
            <h1 className="mt-3 text-2xl font-black text-[#0B132B]">This welcome link isn't valid</h1>
            <p className="mt-3 text-sm text-[#6C757D]">
              If you already set your password, just log in. Otherwise contact your Advantage Warranty Group representative.
            </p>
          </>
        ) : done ? (
          <div className="text-center">
            <CheckCircle2 size={40} className="mx-auto text-emerald-500" />
            <h1 className="mt-3 text-2xl font-black text-[#0B132B]">You're all set</h1>
            <p className="mt-3 text-sm text-[#6C757D]">Redirecting to login…</p>
          </div>
        ) : !data ? (
          <div className="text-sm text-[#6C757D]">Loading…</div>
        ) : (
          <>
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">Welcome</div>
            <h1 className="mt-2 text-2xl font-black text-[#0B132B]">Set your password</h1>
            <p className="mt-3 text-sm text-[#6C757D]">
              You're logging in as <strong className="text-[#0B132B]">{data.email}</strong>
              {data.agency ? <> for <strong className="text-[#0B132B]">{data.agency}</strong></> : null}.
            </p>
            <div className="mt-6 space-y-4">
              <div>
                <Label>New password</Label>
                <Input
                  type="password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  data-testid="agent-welcome-pw"
                />
              </div>
              <div>
                <Label>Confirm password</Label>
                <Input
                  type="password"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && submit()}
                  data-testid="agent-welcome-pw2"
                />
              </div>
              <Button
                onClick={submit}
                disabled={saving}
                className="w-full bg-[#0B132B] hover:bg-[#0B132B]/90 text-white h-11"
                data-testid="agent-welcome-submit"
              >
                {saving ? "Saving…" : "Set password & continue"}
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
